// @ts-strict-ignore
import * as React from 'react';

import { SEARCH_TYPE } from '@/components/AppHeader/hooks/useSearchImplementation';
import { IconContainer } from '@/components/Atoms/IconContainer/IconContainer';
import {
  Menu,
  NeedlMenuButton as Button,
  NeedlMenuCheckboxItem as CheckItem,
  NeedlMenuDivide as Divide,
  NeedlMenuItem as Item,
  NeedlMenuPopover as Popover,
  NeedlSubMenu as SubMenu,
  NeedlSubMenuButton as SubMenuButton,
  NeedlSubMenuPopover as SubMenuPopover,
} from '@/components/Atoms/Menu/RadixMenu';
import * as NeedlBookmark from '@/components/Atoms/NeedlBookmark';
import { Tooltip } from '@/components/Atoms/Tooltip/Tooltip';
import { ConfigUI } from '@/components/Feature';
import * as NoteSelector from '@/components/NoteSelector';
import { useIsOverlayPreview } from '@/components/Preview/hooks/useIsOverlayPreview';
import type {
  DeeplinkType,
  OnClickType,
  ResultType,
  SetOpenMenuType,
} from '@/components/SourceCards/components/common';
import { SummarizationItem } from '@/components/SourceCards/components/SummarizationItem';
import type { ClickHandlerArgs } from '@/components/SourceCards/hooks/useFeedCard';
import { useIsFromNotebookSection } from '@/components/SourceCards/hooks/useIsFromNotebookSection';
import { useOpenFullScreen } from '@/components/SourceCards/hooks/useOpenFullScreen';
import { useShowFullScreenItem } from '@/components/SourceCards/hooks/useShowFullScreenItem';
import { useShowNotesSelector } from '@/components/SourceCards/hooks/useShowNotesSelector';
import { useUserTags } from '@/components/UserTags';
import { useDocumentTags } from '@/components/UserTags/hooks/useDocumentTags';
import { useTagHandler } from '@/components/UserTags/hooks/useTagHandler';
import * as TagsSelector from '@/components/UserTags/TagAutocomplete/TagsSelector';
import type { OnSuccessProps } from '@/components/UserTags/TagAutocomplete/types';
import { useCanAccessComments } from '@/features/Comment';
import { useCommentsClickHandler } from '@/features/Comment/hooks/useCommentsClickHandler';
import { useCreateNotesHandler } from '@/features/Notes/hooks/useCreateNotesHandler';
import { useFeedNotebooks } from '@/features/Notes/hooks/useFeedNotebooks';
import type { Notebook } from '@/features/Notes/types';
import { usePricingContext } from '@/features/Pricing/context';
import { PRICING_FEATURES } from '@/features/Pricing/hooks/useFetchMessaging';
import { useShowSummarize } from '@/features/Summarisation/hooks';
import {
  useConfigUI,
  useFeatureConfigUI,
} from '@/hooks/query/useFeatureConfig';
import { useFeatureFlag } from '@/hooks/query/useFeatureFlag';
import { useResponsiveConstants } from '@/hooks/responsive/useResponsiveConstants';
import { useGetSearchType } from '@/hooks/useGetSearchType';
import { useShowOpenInSource } from '@/hooks/useShowOpenInSource';
import { useShowQuickRemoval } from '@/hooks/useShowQuickRemoval';
import MoreVerticalIcon from '@/icons/more-v.svg?react';
import type { FeedItemType } from '@/types';
import { isViewable } from '@/utils/isViewable';

export const DISABLED_MESSAGE = 'Select a message to access this option';

const CardActionsMenu = ({
  result,
  channelId,
  currentParagraph,
  handleQuickRemoval,
  handleSentimentAnaylisis,
  handleOpenInSource,
  handleOpenInNewTab,
  handleTableExtraction,
  viewSourceFileHandler,
  handlePrint,
  handleShare,
  handleDownload,
  handleCommentClickOverRide,
  hideComments,
  selectedItemsCount,
  selectedItems,
}: CardActionsMenuType) => {
  const [openMenu, setOpenMenu] = React.useState(false);

  const { mobile } = useResponsiveConstants();

  const { isLoading: isPricingLoading } = usePricingContext();
  const { isLoading: isFeaturesLoading } = useFeatureFlag();
  const { isLoading: feedNotebooksLoading } = useFeedNotebooks();
  const { feature } = useFeatureConfigUI('doc_hide_source');

  const { shouldShowQuickRemoval } = useShowQuickRemoval();

  const isNotebookSection = useIsFromNotebookSection(result);

  return (
    <Menu open={openMenu} onOpenChange={setOpenMenu}>
      <Button
        disabled={isPricingLoading || isFeaturesLoading || feedNotebooksLoading}
        aria-label='actions-menu-button'
        id={`menu-button-${result.deeplink}`}
      >
        <MenuButtonContent />
      </Button>
      <Popover>
        <DocumentPrimaryActions
          result={result}
          handleOpenInSource={handleOpenInSource}
          handleOpenInNewTab={handleOpenInNewTab}
          viewSourceFileHandler={viewSourceFileHandler}
          handleSentimentAnaylisis={handleSentimentAnaylisis}
          channelId={channelId}
        />
        <DocumentSectionsDivider
          result={result}
          handleOpenInSource={handleOpenInSource}
          handleOpenInNewTab={handleOpenInNewTab}
          viewSourceFileHandler={viewSourceFileHandler}
          handleSentimentAnaylisis={handleSentimentAnaylisis}
          channelId={channelId}
        />
        <DocumentSecondaryActions
          result={result}
          handleTableExtraction={handleTableExtraction}
        />
        <MetadataActions
          result={result}
          selectedItems={selectedItems}
          setOpenMenu={setOpenMenu}
          currentParagraph={currentParagraph}
          selectedItemsCount={selectedItemsCount}
        />
        <NeedlNotesAction handlePrint={handlePrint} />
        {!mobile && shouldShowQuickRemoval && handleQuickRemoval && feature ? (
          <Divide />
        ) : null}
        <SecondaryActionsItems handleQuickRemoval={handleQuickRemoval} />
        {mobile && !isNotebookSection ? <Divide /> : null}
        <MobilePrimaryActionItems
          hideComments={hideComments}
          result={result}
          handleShare={handleShare}
          handleDownload={handleDownload}
          handleCommentClickOverRide={handleCommentClickOverRide}
        />
      </Popover>
    </Menu>
  );
};

const DocumentSectionsDivider = ({
  channelId,
  result,
  handleOpenInSource,
  handleOpenInNewTab,
  viewSourceFileHandler,
  handleSentimentAnaylisis,
  handlePrint,
}: DocumentSectionsDividerType) => {
  const { mobile } = useResponsiveConstants();
  const { data: features } = useConfigUI();

  const isOverlayPreview = useIsOverlayPreview();

  const showOpenInSourceButton =
    !!useShowOpenInSource({ channelId, result }) &&
    !!handleOpenInSource &&
    !!features.doc_open_in_source;

  const showFullPreview = useShowFullScreenItem();

  const showOpenInNewTab =
    !!result?.mime_type &&
    !!isViewable(result?.mime_type.toLowerCase()) &&
    !!handleOpenInNewTab &&
    !!features.doc_open_in_new_tab;

  const showSentimentAnalysis =
    !!handleSentimentAnaylisis &&
    !!result?.sentiment_link &&
    !!features.doc_sentiment_analysis;

  const showViewSourceFile = !!viewSourceFileHandler && !mobile;

  const isNotebookSectionCard =
    useIsFromNotebookSection(result) && !isOverlayPreview;

  const showSummarize = useShowSummarize({
    form_ua: result?.form_ua,
    source: result?.source,
  });

  const showTableExtraction =
    result?.mime_type === 'pdf' && !!features.doc_table_extraction;

  const showDocumentSecondaryActions = showSummarize || showTableExtraction;

  const showNeedlNotesAction = !!handlePrint;

  const showMetadataActions =
    features?.notebooks || features?.bookmarks || features?.tags;

  if (mobile && !showSentimentAnalysis) {
    return null;
  }

  if (isNotebookSectionCard) {
    return null;
  }

  if (
    (!mobile &&
      !showOpenInSourceButton &&
      !showOpenInNewTab &&
      !showViewSourceFile &&
      !showSentimentAnalysis &&
      !showFullPreview) ||
    (!showDocumentSecondaryActions &&
      !showMetadataActions &&
      !showNeedlNotesAction)
  ) {
    return null;
  }

  return <Divide />;
};

type DocumentSectionsDividerType = HandlePrintType & {
  channelId: string;
  result: FeedItemType;
  handleOpenInSource?: (e: React.MouseEvent) => void;
  handleOpenInNewTab?: (e: React.MouseEvent) => void;
  viewSourceFileHandler?: (e: React.MouseEvent) => void;
  handleSentimentAnaylisis?: (e: React.MouseEvent) => void;
};

const DocumentPrimaryActions = ({
  result,
  handleOpenInSource,
  handleOpenInNewTab,
  viewSourceFileHandler,
  handleSentimentAnaylisis,
  channelId,
}: DocumentPrimaryActionsType) => {
  const { mobile } = useResponsiveConstants();

  return (
    <>
      {!mobile ? (
        <>
          <ConfigUI feature='doc_full_screen_view'>
            <FullScreenItem result={result} />
          </ConfigUI>
          <ConfigUI feature='doc_open_in_source'>
            <OpenInSourceItem
              channelId={channelId}
              onClick={handleOpenInSource}
              result={result}
            />
          </ConfigUI>
          <ConfigUI feature='doc_open_in_new_tab'>
            <OpenInNewTabItem
              onClick={handleOpenInNewTab}
              mimeType={result?.mime_type}
            />
          </ConfigUI>
        </>
      ) : null}
      <ConfigUI feature='doc_sentiment_analysis'>
        <SentimentAnalysisItem
          onClick={handleSentimentAnaylisis}
          sentimentLink={result?.sentiment_link}
        />
      </ConfigUI>
      {!mobile ? <ViewSourceFileItem onClick={viewSourceFileHandler} /> : null}
    </>
  );
};

const DocumentSecondaryActions = ({
  result,
  handleTableExtraction,
}: DocumentSecondaryActionsType) => {
  return (
    <>
      <ConfigUI feature='doc_summarization'>
        <SummarizationItem result={result} />
      </ConfigUI>
      {result?.mime_type === 'pdf' ? (
        <ConfigUI feature='doc_table_extraction'>
          <TableExtractionItem onClick={handleTableExtraction} />
        </ConfigUI>
      ) : null}
    </>
  );
};

const MobilePrimaryActionItems = ({
  hideComments,
  result,
  handleShare,
  handleDownload,
  handleCommentClickOverRide,
}: MobilePrimaryActionItemsType) => {
  const { mobile } = useResponsiveConstants();

  if (!mobile) {
    return null;
  }

  return (
    <>
      <ConfigUI feature='doc_share'>
        <ShareItem onClick={handleShare} />
      </ConfigUI>
      <DownloadItem onClick={handleDownload} />
      {!hideComments ? (
        <CommentsItem
          deeplink={result?.deeplink}
          onClickOverride={handleCommentClickOverRide}
        />
      ) : null}
    </>
  );
};

const MetadataActions = ({
  result,
  setOpenMenu,
  selectedItems,
  selectedItemsCount,
  currentParagraph,
}: MetadataActionsType) => {
  const disableNotesSelector =
    typeof selectedItemsCount === 'number' && selectedItemsCount !== 1;
  return (
    <>
      <ConfigUI feature='notebooks'>
        <NotesSubMenu
          disabled={disableNotesSelector}
          setOpenMenu={setOpenMenu}
          result={selectedItems?.[0] ?? result}
          currentParagraph={currentParagraph}
        />
      </ConfigUI>
      <ConfigUI feature='tags'>
        <TagsSubMenu result={result} setOpenMenu={setOpenMenu} />
      </ConfigUI>
      <ConfigUI feature='bookmarks'>
        <BookmarkItem result={result} />
      </ConfigUI>
    </>
  );
};

const SecondaryActionsItems = ({
  handleQuickRemoval,
}: SecondaryActionsItemsType) => {
  return <QuickRemovalItem onClick={handleQuickRemoval} />;
};

const FullScreenItem = ({ result }: ResultType) => {
  const showFullScreen = useShowFullScreenItem();
  const { openFullScreen } = useOpenFullScreen({
    result,
  });

  if (!showFullScreen) {
    return null;
  }

  return <Item onSelect={openFullScreen}>Open in Full Screen</Item>;
};

const NeedlNotesAction = ({ handlePrint }: NeedlNotesActionType) => {
  return <PrintItem onClick={handlePrint} />;
};

const MenuButtonContent = () => {
  return (
    <IconContainer className='w-5 h-5'>
      <MoreVerticalIcon />
    </IconContainer>
  );
};

const ShareItem = ({ onClick }: OnClickType) => {
  if (!onClick) {
    return null;
  }

  return <Item onClick={onClick}>Share</Item>;
};

const CommentsItemImpl = ({ onClickOverride }: CommentsItemType) => {
  const clickHandler = useCommentsClickHandler({
    overrideClickHandler: onClickOverride,
    cardAction: false,
  });

  return (
    <Item asChild>
      <button onClick={clickHandler}>Comments</button>
    </Item>
  );
};

const CommentsItem = (props: CommentsItemType) => {
  const showComments = useCanAccessComments();

  if (!showComments) {
    return null;
  }

  return <CommentsItemImpl {...props} />;
};

const DownloadItem = ({ onClick }: OnClickType) => {
  if (!onClick) {
    return null;
  }

  return <Item onClick={onClick}>Download</Item>;
};

const OpenInNewTabItem = ({ mimeType, onClick }: OpenInNewTabType) => {
  if (!mimeType || !isViewable(mimeType.toLowerCase()) || !onClick) {
    return null;
  }

  return <Item onClick={onClick}> Open in New Tab </Item>;
};

const OpenInSourceItem = ({ onClick, channelId, result }: OpenInSourceType) => {
  const showButton = useShowOpenInSource({ channelId, result });

  if (!onClick || !showButton) {
    return null;
  }

  return <Item onClick={onClick}>Open in Source</Item>;
};

const SentimentAnalysisItem = ({
  onClick,
  sentimentLink,
}: SentimentAnalysisType) => {
  if (!sentimentLink || !onClick) {
    return null;
  }

  return <Item onClick={onClick}>Sentiment Analysis</Item>;
};

const QuickRemovalItem = ({ onClick }: QuickRemovalType) => {
  const { shouldShowQuickRemoval } = useShowQuickRemoval();

  if (!onClick || !shouldShowQuickRemoval) {
    return null;
  }

  return (
    <ConfigUI feature='doc_hide_source'>
      <Item onClick={onClick}>Hide Sources</Item>
    </ConfigUI>
  );
};

const NotesSubMenuImpl = ({
  disabled = false,
  setOpenMenu,
  result,
  currentParagraph,
}: NotesSubMenuType) => {
  const { withPricing, getActionInfo } = usePricingContext();
  const { isEligible } = getActionInfo(PRICING_FEATURES.add_notebook);

  const createNotes = useCreateNotesHandler({ result, currentParagraph });

  const handleCreateNotes = (notebooks: Notebook[]) => {
    setOpenMenu(false);
    createNotes(notebooks);
  };

  const closeHandler = () => {
    setOpenMenu(false);
  };

  if (!isEligible) {
    return (
      <Item
        onSelect={() =>
          withPricing(PRICING_FEATURES.add_notebook, () => void 0)
        }
        disabled={disabled}
      >
        Add to Notes
      </Item>
    );
  }

  return (
    <SubMenu>
      <Tooltip {...(disabled && { label: DISABLED_MESSAGE })}>
        <SubMenuButton disabled={disabled}>Add to Notes</SubMenuButton>
      </Tooltip>
      <SubMenuPopover avoidCollisions>
        <NoteSelector.Root onSuccess={handleCreateNotes} onClose={closeHandler}>
          <NoteSelector.Selector forceMount portal={false} />
        </NoteSelector.Root>
      </SubMenuPopover>
    </SubMenu>
  );
};

const NotesSubMenu = ({ result, ...rest }: NotesSubMenuType) => {
  const showNotesSubMenu = useShowNotesSelector(result);

  if (!showNotesSubMenu) {
    return null;
  }

  return <NotesSubMenuImpl result={result} {...rest} />;
};

const BookmarkItemImpl = ({ deeplink }: BookmarkItemImplType) => {
  return (
    <NeedlBookmark.Root deeplink={deeplink}>
      <NeedlBookmark.Trigger>
        {({ checked, handleToggle }) => (
          <CheckItem checked={checked} onCheckedChange={handleToggle}>
            Bookmark
          </CheckItem>
        )}
      </NeedlBookmark.Trigger>
    </NeedlBookmark.Root>
  );
};

const BookmarkItem = ({ result }: BookmarkItemType) => {
  const isNotebooksSection = useIsFromNotebookSection(result);
  const serachType = useGetSearchType();
  const isNeedlWebSearch = serachType === SEARCH_TYPE.NEEDL_WEB;

  if (!result?.deeplink || isNotebooksSection || isNeedlWebSearch) {
    return null;
  }

  return <BookmarkItemImpl deeplink={result?.deeplink} />;
};

const TagsSubMenuImpl = ({ deeplink, setOpenMenu }: TagsSubMenuImplType) => {
  const { isLoading: userTagsLoading } = useUserTags();
  const { isLoading: documentTagsLoading } = useDocumentTags(deeplink);
  const successCallback = useTagHandler({ deeplink });

  const handleSuccess = ({
    tags,
    board_id,
    channel_id,
    isBookmarked,
  }: OnSuccessProps) => {
    successCallback({ tags, board_id, channel_id, isBookmarked });
    setOpenMenu(false);
  };

  return (
    <SubMenu>
      <SubMenuButton
        disabled={userTagsLoading || documentTagsLoading}
        id={`tag-button-${deeplink}`}
      >
        Tag
      </SubMenuButton>
      <SubMenuPopover avoidCollisions>
        <TagsSelector.Root deeplink={deeplink} onSuccess={handleSuccess}>
          <TagsSelector.Selector forceMount portal={false} />
        </TagsSelector.Root>
      </SubMenuPopover>
    </SubMenu>
  );
};

const TagsSubMenu = ({ result, setOpenMenu }: TagsSubMenuType) => {
  const isNotebooksSection = useIsFromNotebookSection(result);

  const serachType = useGetSearchType();
  const isNeedlWebSearch = serachType === SEARCH_TYPE.NEEDL_WEB;

  if (!result?.deeplink || isNotebooksSection || isNeedlWebSearch) {
    return null;
  }

  return (
    <TagsSubMenuImpl deeplink={result?.deeplink} setOpenMenu={setOpenMenu} />
  );
};

const TableExtractionItem = ({ onClick }: TableExtractionType) => {
  if (!onClick) {
    return null;
  }

  return <Item onClick={onClick}>Extract Tables</Item>;
};

const ViewSourceFileItem = ({ onClick }: ViewSourceFileType) => {
  if (!onClick) {
    return null;
  }

  return <Item onClick={onClick}>Source File</Item>;
};

const PrintItem = ({ onClick }: PrintItemType) => {
  const { mobile } = useResponsiveConstants();

  if (mobile || !onClick) {
    return null;
  }

  return <Item onClick={onClick}>Print</Item>;
};

type HandlePrintType = {
  handlePrint?: (e: React.MouseEvent) => void;
};

type HandleQuickRemovalType = {
  handleQuickRemoval?: (e: React.MouseEvent) => void;
};

type NeedlNotesActionType = HandlePrintType;

type SecondaryActionsItemsType = HandleQuickRemovalType;

type CommentsItemType = DeeplinkType & {
  onClickOverride?: ({ comments }?: ClickHandlerArgs) => void;
};

type MobilePrimaryActionItemsType = ResultType & {
  hideComments?: boolean;
  handleShare?: (e: React.MouseEvent) => void;
  handleDownload?: (e: React.MouseEvent) => void;
  handleCommentClickOverRide?: ({ comments }?: ClickHandlerArgs) => void;
};

type MetadataActionsType = ResultType &
  SelectedItemsType &
  SetOpenMenuType & {
    currentParagraph?: number;
    selectedItemsCount?: number;
  };

type PrintItemType = OnClickType;

type TagsSubMenuType = ResultType & SetOpenMenuType;
type TagsSubMenuImplType = DeeplinkType & SetOpenMenuType;

type BookmarkItemImplType = DeeplinkType;
type BookmarkItemType = ResultType;

type TableExtractionType = OnClickType;

type CardActionsMenuType = HandlePrintType &
  HandleQuickRemovalType &
  ResultType &
  SelectedItemsType & {
    currentParagraph?: number;
    handleSentimentAnaylisis?: (e: React.MouseEvent) => void;
    handleOpenInSource?: (e: React.MouseEvent) => void;
    handleOpenInNewTab?: (e: React.MouseEvent) => void;
    handleTableExtraction?: (e: React.MouseEvent) => void;
    viewSourceFileHandler?: (e: React.MouseEvent) => void;
    handleShare?: (e: React.MouseEvent) => void;
    handleDownload?: (e: React.MouseEvent) => void;
    handleCommentClickOverRide?: ({ comments }?: ClickHandlerArgs) => void;
    channelId: string;
    hideComments?: boolean;
    selectedItemsCount?: number;
  };

type SelectedItemsType = {
  selectedItems?: FeedItemType[];
};

type OpenInNewTabType = OnClickType & {
  mimeType: string | undefined;
};

type OpenInSourceType = OnClickType &
  ResultType & {
    channelId: string;
  };

type SentimentAnalysisType = OnClickType & {
  sentimentLink: string | undefined;
};

type QuickRemovalType = OnClickType;

type NotesSubMenuType = ResultType &
  SetOpenMenuType & {
    currentParagraph?: number;
    disabled?: boolean;
  };

type ViewSourceFileType = OnClickType;

type DocumentPrimaryActionsType = ResultType & {
  handleOpenInSource?: (e: React.MouseEvent) => void;
  handleOpenInNewTab?: (e: React.MouseEvent) => void;
  viewSourceFileHandler?: (e: React.MouseEvent) => void;
  handleSentimentAnaylisis?: (e: React.MouseEvent) => void;
  channelId: string;
};

type DocumentSecondaryActionsType = ResultType & {
  handleTableExtraction?: (e: React.MouseEvent) => void;
};

export { CardActionsMenu };
